import { useCallback } from 'react';
import { COLUMN_FREEZE_LINE, useAuditLayoutStore } from '../hooks/useAuditLayoutStore';
import { DragDropContext, Draggable, DropResult, Droppable } from 'react-beautiful-dnd';
import { Typography } from '@scale/scaleui';
import cn from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export function AuditColumnOrderingDraggable() {
  const { columnDisplayOrder, setColumnDisplayOrder, sectionVisibility, toggleSectionVisibility } =
    useAuditLayoutStore();

  const handleDragEnd = useCallback(
    (result: DropResult) => {
      if (!result.destination) {
        return;
      }
      const startIndex = result.source.index;
      const endIndex = result.destination.index;
      const newColumnDisplayOrder = [...columnDisplayOrder];
      const [removed] = newColumnDisplayOrder.splice(startIndex, 1);
      newColumnDisplayOrder.splice(endIndex, 0, removed);
      setColumnDisplayOrder(newColumnDisplayOrder);
    },
    [columnDisplayOrder, setColumnDisplayOrder],
  );

  const getItemStyle = (isDragging: boolean, draggableStyle: any) => ({
    userSelect: 'none',
    opacity: isDragging ? 0.1 : 1.0,
    ...draggableStyle,
  });

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable droppableId={`droppable-column-display-order`}>
        {provided => (
          <div className="flex flex-col gap-2" {...provided.droppableProps} ref={provided.innerRef}>
            {columnDisplayOrder.map((section, index) => (
              <Draggable key={section} draggableId={section} index={index}>
                {(provided, snapshot) => {
                  if (section === COLUMN_FREEZE_LINE) {
                    return (
                      <div
                        key="freezeline"
                        className="h-1 my-1 w-full rounded-md bg-neutral-200 flex justify-center items-center"
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                      >
                        <span className="text-xs bg-neutral-0 px-2">Freeze Line</span>
                      </div>
                    );
                  }
                  const isVisible = sectionVisibility[section];
                  return (
                    <div
                      className={cn(
                        'flex items-center justify-between p-2 rounded-md border border-neutral-200 bg-neutral-0',
                        {
                          'bg-neutral-100': snapshot.isDragging,
                        },
                      )}
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
                    >
                      <div className="flex gap-2 items-center">
                        <FontAwesomeIcon icon="grip-vertical" className="text-neutral-500" />
                        <Typography.Subtitle4>{section}</Typography.Subtitle4>
                      </div>
                      <div className="flex gap-2 items-center">
                        <FontAwesomeIcon
                          icon={isVisible ? 'eye' : 'eye-slash'}
                          className="text-neutral-500"
                          onClick={() => toggleSectionVisibility(section)}
                        />
                      </div>
                    </div>
                  );
                }}
              </Draggable>
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
}
